import { canAccessPlan, maxPlan, planIndex } from "./plans.js";

export const DEFAULT_SUMMARY_CUSTOM_INSTRUCTION_LINES = Object.freeze([
  "Summarize the current voice note faithfully.",
  "Keep names, asks, dates, decisions, and next steps.",
  "Use recent chat or context only for spelling, names, references, and timeline.",
  "Write in the sender's voice unless these instructions ask for another style.",
]);

export const DEFAULT_SUMMARY_CUSTOM_INSTRUCTIONS = DEFAULT_SUMMARY_CUSTOM_INSTRUCTION_LINES.join("\n");
export const SUMMARY_CUSTOM_CONTEXT_MAX_LENGTH = 2000;

export const DEFAULT_PREFERENCES = Object.freeze({
  minEnabled: true,
  inboundAutomatic: true,
  outboundAutomatic: true,
  inboundSummary: true,
  inboundTranscript: true,
  outboundSummary: true,
  outboundTranscript: false,
  branding: true,
  groupChats: true,
  summaryDetailMode: "complete",
  summaryContextMode: "chat_context",
  summaryDictionaryEntries: [],
  summaryCustomContext: "",
  summaryCustomInstructions: "",
  voiceNoteMaxDurationSec: 1200,
  inboundSenderKind: "min",
  inboundDestinationKind: "group",
  inboundOwnerCopyMode: "no_chat_summary",
  inboundDeliveryGroupJid: "",
  inboundDeliveryGroupName: "Min inbox",
  inboundDeliveryGroupPictureStatus: "pending",
  changelogUpdatesEnabled: true,
  minInboxReactionRelayEnabled: false,
  minInboxReplyRelayEnabled: false,
  chatRules: [],
});

function normalizeComparableChatRules(value) {
  return normalizeChatRules(value)
    .map((rule) => JSON.stringify(rule))
    .sort();
}

export function preferencesMatch(left = DEFAULT_PREFERENCES, right = DEFAULT_PREFERENCES) {
  const normalizedLeft = normalizePreferences(left);
  const normalizedRight = normalizePreferences(right);
  const leftChatRules = normalizeComparableChatRules(normalizedLeft.chatRules);
  const rightChatRules = normalizeComparableChatRules(normalizedRight.chatRules);

  return (
    normalizedLeft.minEnabled === normalizedRight.minEnabled
    && normalizedLeft.inboundAutomatic === normalizedRight.inboundAutomatic
    && normalizedLeft.outboundAutomatic === normalizedRight.outboundAutomatic
    && normalizedLeft.inboundSummary === normalizedRight.inboundSummary
    && normalizedLeft.inboundTranscript === normalizedRight.inboundTranscript
    && normalizedLeft.outboundSummary === normalizedRight.outboundSummary
    && normalizedLeft.outboundTranscript === normalizedRight.outboundTranscript
    && normalizedLeft.branding === normalizedRight.branding
    && normalizedLeft.groupChats === normalizedRight.groupChats
    && normalizedLeft.summaryDetailMode === normalizedRight.summaryDetailMode
    && normalizedLeft.summaryContextMode === normalizedRight.summaryContextMode
    && normalizedLeft.summaryDictionaryEntries.join("\n") === normalizedRight.summaryDictionaryEntries.join("\n")
    && normalizedLeft.summaryCustomContext === normalizedRight.summaryCustomContext
    && normalizedLeft.summaryCustomInstructions === normalizedRight.summaryCustomInstructions
    && normalizedLeft.voiceNoteMaxDurationSec === normalizedRight.voiceNoteMaxDurationSec
    && normalizedLeft.inboundSenderKind === normalizedRight.inboundSenderKind
    && normalizedLeft.inboundDestinationKind === normalizedRight.inboundDestinationKind
    && normalizedLeft.inboundOwnerCopyMode === normalizedRight.inboundOwnerCopyMode
    && normalizedLeft.changelogUpdatesEnabled === normalizedRight.changelogUpdatesEnabled
    && normalizedLeft.minInboxReactionRelayEnabled === normalizedRight.minInboxReactionRelayEnabled
    && normalizedLeft.minInboxReplyRelayEnabled === normalizedRight.minInboxReplyRelayEnabled
    && leftChatRules.length === rightChatRules.length
    && leftChatRules.every((value, index) => value === rightChatRules[index])
  );
}

export function hasUnsavedPreferenceChanges(currentPreferences, savedPreferences = DEFAULT_PREFERENCES) {
  return !preferencesMatch(currentPreferences, savedPreferences);
}

export function hasCustomizedPreferences(preferences = DEFAULT_PREFERENCES) {
  return hasUnsavedPreferenceChanges(preferences, DEFAULT_PREFERENCES);
}

export function getPlanSeededPreferences(plan, preferences = DEFAULT_PREFERENCES) {
  const normalized = normalizePreferences(preferences);

  if (planIndex(plan) >= planIndex("fun")) {
    normalized.inboundSummary = true;
    normalized.inboundTranscript = true;
  }

  return normalized;
}

export function normalizeChatRuleDeliveryMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "off" || normalized === "summary" || normalized === "transcript" || normalized === "both") {
    return normalized;
  }
  return "inherit";
}

export function normalizeChatRuleBrandingMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "on" || normalized === "off") {
    return normalized;
  }
  return "inherit";
}

export function normalizeDeliveryLanguageMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === "auto"
    || normalized === "source_only"
    || normalized === "dual_summary"
    || normalized === "translate_all"
  ) {
    return normalized;
  }
  return "auto";
}

export function normalizeSummaryDetailMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["main_points", "complete", "bullets", "flow", "custom"].includes(normalized)) {
    return normalized;
  }
  return "complete";
}

export function normalizeSummaryContextMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "transcript_only") {
    return "transcript_only";
  }
  return "chat_context";
}

export function normalizeSummaryDictionaryEntries(value, { maxChars = SUMMARY_CUSTOM_CONTEXT_MAX_LENGTH } = {}) {
  const rawEntries = Array.isArray(value) ? value : String(value || "").split("\n");
  const entries = [];
  const seen = new Set();
  let totalChars = 0;

  for (const rawEntry of rawEntries) {
    const cleaned = String(rawEntry || "").trim().replace(/\s+/g, " ");
    if (!cleaned || seen.has(cleaned)) {
      continue;
    }

    const separatorChars = entries.length > 0 ? 1 : 0;
    const remainingChars = maxChars - totalChars - separatorChars;
    if (remainingChars <= 0) {
      break;
    }

    const entry = cleaned.length > remainingChars ? cleaned.slice(0, remainingChars).trim() : cleaned;
    if (!entry) {
      break;
    }
    entries.push(entry);
    seen.add(entry);
    totalChars += separatorChars + entry.length;
  }

  return entries;
}

export function normalizeSummaryCustomContext(value) {
  return normalizeSummaryDictionaryEntries(value).join("\n");
}

export function normalizeSummaryCustomInstructions(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim().replace(/\s+/g, " "))
    .filter(Boolean)
    .join("\n")
    .slice(0, 4000);
}

export function normalizeVoiceNoteMaxDurationSec(value) {
  const normalized = Number.parseInt(value, 10);
  if ([1200, 3600, 7200].includes(normalized)) {
    return normalized;
  }
  return 1200;
}

export function normalizeInboundSenderKind(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "self" || normalized === "min") {
    return DEFAULT_PREFERENCES.inboundSenderKind;
  }
  return DEFAULT_PREFERENCES.inboundSenderKind;
}

export function normalizeInboundDestinationKind(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "personal" || normalized === "group") {
    return DEFAULT_PREFERENCES.inboundDestinationKind;
  }
  return DEFAULT_PREFERENCES.inboundDestinationKind;
}

export function normalizeInboundOwnerCopyMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "always" || normalized === "no_chat_summary" || normalized === "different_outputs") {
    return normalized;
  }
  return DEFAULT_PREFERENCES.inboundOwnerCopyMode;
}

export function normalizeInboundGroupPictureStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === "pending"
    || normalized === "set"
    || normalized === "unavailable"
    || normalized === "failed"
  ) {
    return normalized;
  }
  return DEFAULT_PREFERENCES.inboundDeliveryGroupPictureStatus;
}

export function normalizeChatRules(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const deduped = new Map();
  for (const rule of value) {
    if (!rule || typeof rule !== "object") {
      continue;
    }
    const chatId = String(rule.chatId ?? rule.chat_id ?? "").trim();
    if (!chatId) {
      continue;
    }
    const normalizedRule = {
      chatId,
      inboundMode: normalizeChatRuleDeliveryMode(rule.inboundMode ?? rule.inbound_mode),
      outboundMode: normalizeChatRuleDeliveryMode(rule.outboundMode ?? rule.outbound_mode),
      brandingMode: normalizeChatRuleBrandingMode(rule.brandingMode ?? rule.branding_mode),
      targetLanguageCode: String(rule.targetLanguageCode ?? rule.target_language_code ?? "").trim(),
      deliveryLanguageMode: normalizeDeliveryLanguageMode(
        rule.deliveryLanguageMode ?? rule.delivery_language_mode,
      ),
    };
    deduped.set(chatId, normalizedRule);
  }
  return [...deduped.values()];
}

function summaryDictionarySource(preferences = {}) {
  const entries = preferences.summaryDictionaryEntries ?? preferences.summary_dictionary_entries;
  const legacyContext = preferences.summaryCustomContext ?? preferences.summary_custom_context;
  return entries ?? legacyContext ?? DEFAULT_PREFERENCES.summaryDictionaryEntries;
}

export function normalizePreferences(preferences = {}) {
  const inboundSenderKind = normalizeInboundSenderKind(
    preferences.inboundSenderKind
    ?? preferences.inbound_sender_kind
    ?? DEFAULT_PREFERENCES.inboundSenderKind,
  );
  const inboundDestinationKind = normalizeInboundDestinationKind(
    preferences.inboundDestinationKind
    ?? preferences.inbound_destination_kind
    ?? DEFAULT_PREFERENCES.inboundDestinationKind,
  );
  const summaryDictionaryEntries = normalizeSummaryDictionaryEntries(summaryDictionarySource(preferences));

  return {
    minEnabled: preferences.minEnabled !== false,
    inboundAutomatic: (
      preferences.inboundAutomatic
      ?? preferences.inboundAutomaticEnabled
      ?? preferences.inbound_automatic_enabled
      ?? DEFAULT_PREFERENCES.inboundAutomatic
    ) !== false,
    outboundAutomatic: (
      preferences.outboundAutomatic
      ?? preferences.outboundAutomaticEnabled
      ?? preferences.outbound_automatic_enabled
      ?? DEFAULT_PREFERENCES.outboundAutomatic
    ) !== false,
    inboundSummary: Boolean(preferences.inboundSummary ?? DEFAULT_PREFERENCES.inboundSummary),
    inboundTranscript: Boolean(preferences.inboundTranscript ?? DEFAULT_PREFERENCES.inboundTranscript),
    outboundSummary: preferences.outboundSummary !== false,
    outboundTranscript: Boolean(preferences.outboundTranscript ?? DEFAULT_PREFERENCES.outboundTranscript),
    branding: preferences.branding !== false,
    groupChats: preferences.groupChats !== false,
    summaryDetailMode: normalizeSummaryDetailMode(
      preferences.summaryDetailMode
      ?? preferences.summary_detail_mode
      ?? DEFAULT_PREFERENCES.summaryDetailMode,
    ),
    summaryContextMode: normalizeSummaryContextMode(
      preferences.summaryContextMode
      ?? preferences.summary_context_mode
      ?? DEFAULT_PREFERENCES.summaryContextMode,
    ),
    summaryDictionaryEntries,
    summaryCustomContext: normalizeSummaryCustomContext(summaryDictionaryEntries),
    summaryCustomInstructions: normalizeSummaryCustomInstructions(
      preferences.summaryCustomInstructions
      ?? preferences.summary_custom_instructions
      ?? DEFAULT_PREFERENCES.summaryCustomInstructions,
    ),
    voiceNoteMaxDurationSec: normalizeVoiceNoteMaxDurationSec(
      preferences.voiceNoteMaxDurationSec
      ?? preferences.voice_note_max_duration_sec
      ?? DEFAULT_PREFERENCES.voiceNoteMaxDurationSec,
    ),
    inboundSenderKind,
    inboundDestinationKind,
    inboundOwnerCopyMode: normalizeInboundOwnerCopyMode(
      preferences.inboundOwnerCopyMode
      ?? preferences.inbound_owner_copy_mode
      ?? DEFAULT_PREFERENCES.inboundOwnerCopyMode,
    ),
    inboundDeliveryGroupJid: String(
      preferences.inboundDeliveryGroupJid
      ?? preferences.inbound_delivery_group_jid
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupJid,
    ).trim(),
    inboundDeliveryGroupName: String(
      preferences.inboundDeliveryGroupName
      ?? preferences.inbound_delivery_group_name
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupName,
    ).trim() || DEFAULT_PREFERENCES.inboundDeliveryGroupName,
    inboundDeliveryGroupPictureStatus: normalizeInboundGroupPictureStatus(
      preferences.inboundDeliveryGroupPictureStatus
      ?? preferences.inbound_delivery_group_picture_status
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupPictureStatus,
    ),
    changelogUpdatesEnabled: (
      preferences.changelogUpdatesEnabled
      ?? preferences.changelog_updates_enabled
      ?? DEFAULT_PREFERENCES.changelogUpdatesEnabled
    ) !== false,
    minInboxReactionRelayEnabled: Boolean(
      preferences.minInboxReactionRelayEnabled
      ?? preferences.min_inbox_reaction_relay_enabled
      ?? DEFAULT_PREFERENCES.minInboxReactionRelayEnabled,
    ),
    minInboxReplyRelayEnabled: Boolean(
      preferences.minInboxReplyRelayEnabled
      ?? preferences.min_inbox_reply_relay_enabled
      ?? DEFAULT_PREFERENCES.minInboxReplyRelayEnabled,
    ),
    chatRules: normalizeChatRules(preferences.chatRules),
  };
}

export function preferencesFromApi(preferences = {}) {
  return normalizePreferences({
    minEnabled:
      preferences.minEnabled
      ?? preferences.min_enabled
      ?? preferences.enabled
      ?? true,
    inboundSummary:
      preferences.inboundSummary
      ?? preferences.inboundSummaryEnabled
      ?? preferences.inbound_summary_enabled
      ?? DEFAULT_PREFERENCES.inboundSummary,
    inboundAutomatic:
      preferences.inboundAutomatic
      ?? preferences.inboundAutomaticEnabled
      ?? preferences.inbound_automatic_enabled
      ?? DEFAULT_PREFERENCES.inboundAutomatic,
    outboundAutomatic:
      preferences.outboundAutomatic
      ?? preferences.outboundAutomaticEnabled
      ?? preferences.outbound_automatic_enabled
      ?? DEFAULT_PREFERENCES.outboundAutomatic,
    inboundTranscript:
      preferences.inboundTranscript
      ?? preferences.inboundTranscriptEnabled
      ?? preferences.inbound_transcript_enabled
      ?? DEFAULT_PREFERENCES.inboundTranscript,
    outboundSummary:
      preferences.outboundSummary
      ?? preferences.outboundSummaryEnabled
      ?? preferences.outbound_summary_enabled
      ?? true,
    outboundTranscript:
      preferences.outboundTranscript
      ?? preferences.outboundTranscriptEnabled
      ?? preferences.outbound_transcript_enabled
      ?? false,
    branding:
      preferences.branding
      ?? preferences.brandingEnabled
      ?? preferences.branding_enabled
      ?? true,
    groupChats:
      preferences.groupChats
      ?? preferences.groupsEnabled
      ?? preferences.groups_enabled
      ?? true,
    summaryDetailMode:
      preferences.summaryDetailMode
      ?? preferences.summary_detail_mode
      ?? DEFAULT_PREFERENCES.summaryDetailMode,
    summaryContextMode:
      preferences.summaryContextMode
      ?? preferences.summary_context_mode
      ?? DEFAULT_PREFERENCES.summaryContextMode,
    summaryDictionaryEntries:
      summaryDictionarySource(preferences),
    summaryCustomContext:
      preferences.summaryCustomContext
      ?? preferences.summary_custom_context
      ?? DEFAULT_PREFERENCES.summaryCustomContext,
    summaryCustomInstructions:
      preferences.summaryCustomInstructions
      ?? preferences.summary_custom_instructions
      ?? DEFAULT_PREFERENCES.summaryCustomInstructions,
    voiceNoteMaxDurationSec:
      preferences.voiceNoteMaxDurationSec
      ?? preferences.voice_note_max_duration_sec
      ?? DEFAULT_PREFERENCES.voiceNoteMaxDurationSec,
    inboundSenderKind:
      preferences.inboundSenderKind
      ?? preferences.inbound_sender_kind
      ?? DEFAULT_PREFERENCES.inboundSenderKind,
    inboundDestinationKind:
      preferences.inboundDestinationKind
      ?? preferences.inbound_destination_kind
      ?? DEFAULT_PREFERENCES.inboundDestinationKind,
    inboundOwnerCopyMode:
      preferences.inboundOwnerCopyMode
      ?? preferences.inbound_owner_copy_mode
      ?? DEFAULT_PREFERENCES.inboundOwnerCopyMode,
    inboundDeliveryGroupJid:
      preferences.inboundDeliveryGroupJid
      ?? preferences.inbound_delivery_group_jid
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupJid,
    inboundDeliveryGroupName:
      preferences.inboundDeliveryGroupName
      ?? preferences.inbound_delivery_group_name
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupName,
    inboundDeliveryGroupPictureStatus:
      preferences.inboundDeliveryGroupPictureStatus
      ?? preferences.inbound_delivery_group_picture_status
      ?? DEFAULT_PREFERENCES.inboundDeliveryGroupPictureStatus,
    changelogUpdatesEnabled:
      preferences.changelogUpdatesEnabled
      ?? preferences.changelog_updates_enabled
      ?? DEFAULT_PREFERENCES.changelogUpdatesEnabled,
    minInboxReactionRelayEnabled:
      preferences.minInboxReactionRelayEnabled
      ?? preferences.min_inbox_reaction_relay_enabled
      ?? DEFAULT_PREFERENCES.minInboxReactionRelayEnabled,
    minInboxReplyRelayEnabled:
      preferences.minInboxReplyRelayEnabled
      ?? preferences.min_inbox_reply_relay_enabled
      ?? DEFAULT_PREFERENCES.minInboxReplyRelayEnabled,
    chatRules:
      preferences.chatRules
      ?? preferences.chat_rules
      ?? [],
  });
}

export function preferencesToApi(preferences) {
  const normalized = normalizePreferences(preferences);
  return {
    minEnabled: normalized.minEnabled,
    inboundAutomaticEnabled: normalized.inboundAutomatic,
    outboundAutomaticEnabled: normalized.outboundAutomatic,
    inboundSummary: normalized.inboundSummary,
    inboundTranscript: normalized.inboundTranscript,
    outboundSummary: normalized.outboundSummary,
    outboundTranscript: normalized.outboundTranscript,
    branding: normalized.branding,
    groupChats: normalized.groupChats,
    summaryDetailMode: normalized.summaryDetailMode,
    summaryContextMode: normalized.summaryContextMode,
    summaryDictionaryEntries: normalized.summaryDictionaryEntries,
    summaryCustomContext: normalized.summaryCustomContext,
    summaryCustomInstructions: normalized.summaryCustomInstructions,
    voiceNoteMaxDurationSec: normalized.voiceNoteMaxDurationSec,
    inboundSenderKind: normalized.inboundSenderKind,
    inboundDestinationKind: normalized.inboundDestinationKind,
    inboundOwnerCopyMode: normalized.inboundOwnerCopyMode,
    inboundDeliveryGroupJid: normalized.inboundDeliveryGroupJid,
    inboundDeliveryGroupName: normalized.inboundDeliveryGroupName,
    inboundDeliveryGroupPictureStatus: normalized.inboundDeliveryGroupPictureStatus,
    changelogUpdatesEnabled: normalized.changelogUpdatesEnabled,
    minInboxReactionRelayEnabled: normalized.minInboxReactionRelayEnabled,
    minInboxReplyRelayEnabled: normalized.minInboxReplyRelayEnabled,
    chatRules: normalized.chatRules,
  };
}

export function getRequiredPlanForPreferences(preferences = DEFAULT_PREFERENCES) {
  const normalized = normalizePreferences(preferences);
  if (normalized.minEnabled === false) {
    return "free";
  }
  let requiredPlan = "free";

  if (normalized.inboundSummary || normalized.inboundTranscript || !normalized.outboundSummary || normalized.outboundTranscript) {
    requiredPlan = maxPlan(requiredPlan, "fun");
  }

  if (!normalized.inboundAutomatic || !normalized.outboundAutomatic) {
    requiredPlan = maxPlan(requiredPlan, "fun");
  }

  if (normalized.branding === false) {
    requiredPlan = maxPlan(requiredPlan, "pro");
  }

  if (
    normalized.summaryDetailMode !== DEFAULT_PREFERENCES.summaryDetailMode
    || normalized.summaryContextMode !== DEFAULT_PREFERENCES.summaryContextMode
    || normalized.summaryDictionaryEntries.length > 0
    || normalized.summaryCustomContext
    || normalized.summaryCustomInstructions
  ) {
    requiredPlan = maxPlan(requiredPlan, "fun");
  }

  if (normalized.voiceNoteMaxDurationSec > DEFAULT_PREFERENCES.voiceNoteMaxDurationSec) {
    requiredPlan = maxPlan(requiredPlan, "pro");
  }

  if (normalized.groupChats !== DEFAULT_PREFERENCES.groupChats) {
    requiredPlan = maxPlan(requiredPlan, "fun");
  }

  if (
    normalized.inboundOwnerCopyMode !== DEFAULT_PREFERENCES.inboundOwnerCopyMode
  ) {
    requiredPlan = maxPlan(requiredPlan, "fun");
  }

  if (normalized.chatRules.length > 0) {
    const hasPaidOnlyOverrides = normalized.chatRules.some((rule) => (
      rule.inboundMode !== "inherit"
      || rule.outboundMode !== "inherit"
      || rule.brandingMode !== "inherit"
    ));
    if (hasPaidOnlyOverrides) {
      requiredPlan = maxPlan(requiredPlan, "pro");
    }
  }

  return requiredPlan;
}

export function canSavePreferencesForPlan(preferences, effectivePlan) {
  return canAccessPlan(getRequiredPlanForPreferences(preferences), effectivePlan);
}
